import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Calendar, User, ArrowRight, Filter, Building, Loader2 } from 'lucide-react';
import { useProjects, useAdditionalProjects } from '../hooks/usePortfolioData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('year');

  const { projects } = useProjects();
  const { projects: additionalProjects, loading: additionalLoading } = useAdditionalProjects();

  const projectTypes = ['all', 'Luxury Retail', 'Corporate Office', 'Restaurant & Cultural', 'Large Scale Retail'];

  // Separate projects with photos (Featured Projects) and projects without photos (Other Projects)
  const projectsWithPhotos = projects.filter(project =>
    project.images &&
    project.images.main
  );

  const projectsWithoutPhotos = projects.filter(project =>
    !project.images || !project.images.main
  );

  const filteredWithPhotos = projectsWithPhotos.filter(project =>
    filter === 'all' || project.type === filter
  );

  const sortedWithPhotos = [...filteredWithPhotos].sort((a, b) => {
    if (sortBy === 'year') return b.year.localeCompare(a.year);
    if (sortBy === 'area') return parseInt(b.area) - parseInt(a.area);
    return a.name.localeCompare(b.name);
  });

  const combinedOtherProjects = [...projectsWithoutPhotos, ...additionalProjects];
  const sortedWithoutPhotos = combinedOtherProjects.filter(project =>
    filter === 'all' || project.type === filter
  ).sort((a, b) => {
    if (sortBy === 'year') return b.year.localeCompare(a.year);
    if (sortBy === 'area') return parseInt(b.area) - parseInt(a.area);
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Portfolio</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A comprehensive showcase of architectural projects spanning retail, corporate,
              and cultural spaces across Egypt and Saudi Arabia.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter:</span>
            </div>
            {projectTypes.map((type) => (
              <Button
                key={type}
                variant={filter === type ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(type)}
              >
                {type === 'all' ? 'All Projects' : type}
              </Button>
            ))}
          </div>

          {/* Sort */}
          <div className="flex justify-center mb-8">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="year">Sort by Year</option>
              <option value="area">Sort by Area</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Featured Projects</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {sortedWithPhotos.map((project) => (
              <Card key={project.slug} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative">
                  {project.images?.main ? (
                    <img
                      src={project.images.main || project.images.render}
                      alt={project.name}
                      className="w-full h-64 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <Building className="text-gray-400" size={48} />
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary">{project.status}</Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    <Badge>{project.year}</Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-xl mb-2">{project.name}</h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <MapPin size={14} />
                          <span>{project.location}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User size={14} />
                          <span>{project.role}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">{project.area}</div>
                      <div className="text-sm text-gray-500">{project.type}</div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4 line-clamp-3">{project.description}</p>

                  {project.keyAchievements && project.keyAchievements.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.keyAchievements.slice(0, 2).map((achievement, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {achievement}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Button asChild className="w-full">
                    <Link to={`/projects/${project.slug}`}>
                      View Project Details <ArrowRight className="ml-2" size={16} />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Other Projects</h2>
          {additionalLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {sortedWithoutPhotos.map((project, index) => (
                <Card key={project.slug || index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-2">{project.name}</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center space-x-2">
                        <MapPin size={14} />
                        <span>{project.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar size={14} />
                        <span>{project.year}</span>
                      </div>
                    </div>
                    <div className="mt-4 text-lg font-semibold text-gray-900">{project.area}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;
